/**
 * What the picture allowance (plan `2026_10_09_ovid_first_offer.md` § Decided 4, § Design 7; board `OvidDailyLimit`)
 * makes of the wall's bar, all inside the bar's own row: the last free picture said before it is spent, the verb
 * becoming the plan's door once the free ones are gone, the welcome back from the plan, and a subscriber's fair-use
 * cap with nothing to sell. Mounted by `HouseTheme.allowanceBar`.
 */
import {useEffect, useState} from 'react'
import {useRouteLoaderData} from 'react-router'
import type {loader as rootLoader} from '@root'
import {RETURN_SESSION_PARAM} from '@core/billing/checkoutReturn'
import type {AllowanceState} from '@core/wall/contract'
import type {AllowanceReturn, BarAllowance, BarAllowanceContext} from '@core/wall/ui/theme'
import {PLAN_RETURN_PARAM} from '@product/ovid/components/ModelIntake/checkoutReturn'
import {planPerMonth} from '@product/ovid/proposition'
import {ovidTrack} from '@product/ovid/utils/analytics'

/** The checkout's return: `plan=success` (paid) or `plan=active` (already held) is welcomed; every `plan=` is stripped. */
export const OVID_ALLOWANCE_RETURN: AllowanceReturn = {
    param: PLAN_RETURN_PARAM,
    values: ['success', 'active'],
    alsoStrip: [RETURN_SESSION_PARAM],
}

const clock = (iso: string) =>
    new Intl.DateTimeFormat(undefined, {hour: '2-digit', minute: '2-digit', hourCycle: 'h23'}).format(new Date(iso))

/** HH:MM in the viewer's own time. Said after mount: the server does not know the viewer's zone. */
function LocalTime({iso}: {iso: string | null}) {
    const [text, setText] = useState<string | null>(null)
    useEffect(() => setText(iso ? clock(iso) : null), [iso])
    return <>{iso ? (text ?? '--:--') : 'later'}</>
}

/** The plan's price in the viewer's currency, as the intake says it. */
function PlanPrice() {
    const rootData = useRouteLoaderData<typeof rootLoader>('root')
    return <small>{planPerMonth(rootData?.modelCurrency ?? 'usd')}</small>
}

export function ovidAllowanceBar({remaining, hasPlan, resetsAt}: AllowanceState, {welcome, returnTo}: BarAllowanceContext): BarAllowance | null {
    if (remaining === 0) {
        if (hasPlan) {
            return {
                note: ['daily limit', 'reached'],
                verb: {label: <>More at <LocalTime iso={resetsAt} /></>, disabled: true},
            }
        }
        return {
            note: ['no free pictures left', <>more at <b><LocalTime iso={resetsAt} /></b></>],
            verb: {
                label: <>Get the plan<PlanPrice /></>,
                post: {
                    action: '/checkout/plan',
                    fields: {returnTo},
                    onSubmit: () => ovidTrack('checkout_started', {kind: 'plan'}),
                },
            },
        }
    }
    if (hasPlan) return welcome ? {note: ['plan active', 'many more today'], lit: true} : null
    return remaining === 1 ? {note: ['1 free picture', 'left today']} : null
}
